/**
 * Books by status — one stacked bar plus a per-status legend.
 *
 * Hand-drawn with MUI Boxes rather than a chart component: seven segments of a
 * single 100% bar is layout, not plotting, and this way each segment inherits a
 * theme palette colour so both themes and every future palette tweak follow for
 * free. `suspended` is deliberately NOT in the bar — it is not a lifecycle
 * stage, it is an intervention — so it gets its own callout underneath.
 */

import React from 'react';
import { Box, Card, CardContent, Typography, Tooltip, Stack, useTheme } from '@mui/material';
import { STATUS_META, STATUS_FUNNEL_ORDER, num } from '../weddingFormat';
import type { BooksByStatus, BookStatus } from '../weddingApi';
import { WeddingStatusChip } from '../WeddingChips';

interface Props {
  books: BooksByStatus;
}

const StatusFunnelCard: React.FC<Props> = ({ books }) => {
  const theme = useTheme();

  const segments = STATUS_FUNNEL_ORDER.map((status) => ({
    status,
    count: num(books[status as keyof BooksByStatus] as number),
  }));
  const barTotal = segments.reduce((sum, s) => sum + s.count, 0);

  /** MUI palette key → an actual colour, without touching a hex literal. */
  const colorFor = (status: BookStatus): string => {
    const key = STATUS_META[status].color;
    if (key === 'default') return theme.palette.action.disabled;
    return theme.palette[key].main;
  };

  return (
    <Card sx={{ height: '100%', borderRadius: '16px' }}>
      <CardContent>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
          Books by status
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {barTotal} book{barTotal === 1 ? '' : 's'} in the lifecycle
          {books.suspended > 0 ? `, ${books.suspended} suspended` : ''}
        </Typography>

        {barTotal === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No books yet.
          </Typography>
        ) : (
          <Box
            role="img"
            aria-label={segments
              .filter((s) => s.count > 0)
              .map((s) => `${STATUS_META[s.status].label}: ${s.count}`)
              .join(', ')}
            sx={{ display: 'flex', height: 20, borderRadius: 1, overflow: 'hidden', mb: 2 }}
          >
            {segments
              .filter((s) => s.count > 0)
              .map((s) => (
                <Tooltip key={s.status} title={`${STATUS_META[s.status].label}: ${s.count}`}>
                  <Box
                    sx={{
                      width: `${(s.count / barTotal) * 100}%`,
                      backgroundColor: colorFor(s.status),
                    }}
                  />
                </Tooltip>
              ))}
          </Box>
        )}

        <Stack spacing={1}>
          {segments.map((s) => (
            <Box
              key={s.status}
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}
            >
              <WeddingStatusChip status={s.status} />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {s.count}
              </Typography>
            </Box>
          ))}
        </Stack>

        {books.suspended > 0 && (
          <Box
            sx={{
              mt: 2,
              p: 1.5,
              borderRadius: 1,
              border: 1,
              borderColor: 'error.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="body2" color="error.main" sx={{ fontWeight: 600 }}>
              Suspended
            </Typography>
            <Typography variant="body2" color="error.main" sx={{ fontWeight: 700 }}>
              {books.suspended}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default StatusFunnelCard;
